import type { ReactNode } from 'react'
import type { BodyKey, MoonFact } from '@/constants/solar-system'
import type { OrreryEngine, OrreryState } from '@/lib/three/orrery-engine'

import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUpRight, Minus, Pause, Play, Plus, RotateCcw, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { supportsWebGL } from '@/lib/webgl'
import { FadeLink } from '@/components/generals/fade-link'
import { PlanetGlobe } from '@/components/orrery/planet-globe'
import { MISSIONS, MOONS, SOLAR_BODIES, bodyByKey } from '@/constants/solar-system'

interface OrreryProps {
    variant?: 'home' | 'page'
}

const YEAR_SECONDS = 31557600
const MAX_LOG = Math.log10(YEAR_SECONDS)
const AU_KM = 149597870.7

const rateFor = (value: number) => Math.pow(10, (value / 100) * MAX_LOG)

const describeRate = (rate: number): string => {
    if (rate < 1.5) return 'Real time'

    const units: [number, string][] = [
        [YEAR_SECONDS, 'year'],
        [2629800, 'month'],
        [604800, 'week'],
        [86400, 'day'],
        [3600, 'hour'],
        [60, 'minute'],
        [1, 'second']
    ]

    for (const [size, name] of units) {
        if (rate >= size * 0.95) {
            const count = rate / size
            const rounded = count >= 10 ? Math.round(count) : Math.round(count * 10) / 10

            return `${rounded} ${name}${rounded === 1 ? '' : 's'} per second`
        }
    }

    return 'Real time'
}

const formatAu = (au: number) => (au < 10 ? au.toFixed(3) : au.toFixed(2))

const lightTime = (au: number): string => {
    const minutes = au * 8.3167

    if (minutes < 1) return `${Math.round(minutes * 60)} light-seconds`
    if (minutes < 90) return `${minutes.toFixed(1)} light-minutes`

    return `${(minutes / 60).toFixed(1)} light-hours`
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Formats a YYYY-MM-DD launch date by hand, so server and browser render identical text. */
const formatLaunch = (date: string): string => {
    const [year, month, day] = date.split('-').map(Number)

    return `${day} ${MONTHS[month - 1]} ${year}`
}

const simDateFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric' })
const simTimeFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })

/** A 3D orrery driven by real planetary positions. */
export const Orrery = ({ variant = 'home' }: OrreryProps): ReactNode => {
    const hostRef = useRef<HTMLDivElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const engineRef = useRef<OrreryEngine | null>(null)
    const labelRefs = useRef(new Map<string, HTMLElement>())
    const [focused, setFocused] = useState<BodyKey | null>(null)
    const [moon, setMoon] = useState<MoonFact | null>(null)
    const [paused, setPaused] = useState(false)
    const [speed, setSpeed] = useState(77)
    const [scale, setScale] = useState<'readable' | 'true'>('readable')
    const [missions, setMissions] = useState(false)
    const [engaged, setEngaged] = useState(variant === 'page')
    const [state, setState] = useState<OrreryState | null>(null)
    const [failed, setFailed] = useState(false)
    const [listOpen, setListOpen] = useState(false)
    const choicesRef = useRef({ paused, speed, scale, missions, focused, engaged })

    choicesRef.current = { paused, speed, scale, missions, focused, engaged }

    const select = useCallback((key: BodyKey | null) => {
        setMoon(null)
        setFocused(key)
        engineRef.current?.focus(key)
    }, [])

    useEffect(() => {
        const canvas = canvasRef.current
        const host = hostRef.current

        if (!canvas || !host) return

        let cancelled = false
        let observer: IntersectionObserver | null = null
        let resizeObserver: ResizeObserver | null = null
        let poll = 0

        const start = async () => {
            if (!supportsWebGL()) {
                setFailed(true)
                return
            }

            try {
                const { createOrrery } = await import('@/lib/three/orrery-engine')
                const mobile = window.matchMedia('(max-width: 768px)').matches
                const engine = await createOrrery({
                    canvas,
                    host,
                    mobile,
                    onSelect: key => select(key),
                    onEngage: () => setEngaged(true)
                })

                if (cancelled) {
                    engine.dispose()
                    return
                }

                // Apply anything the visitor chose while the engine was still loading.
                const choices = choicesRef.current

                engineRef.current = engine
                engine.setLabels(labelRefs.current)
                engine.setSpeed(rateFor(choices.speed))
                engine.setWheelZoom(variant === 'page' || choices.engaged)

                if (choices.paused) engine.setPaused(true)
                if (choices.scale !== 'readable') engine.setScaleMode(choices.scale)
                if (choices.missions) engine.setMissions(true)
                if (choices.focused) engine.focus(choices.focused)

                observer = new IntersectionObserver(([entry]) => engine.setActive(entry.isIntersecting), { rootMargin: '80px 0px' })
                observer.observe(host)
                resizeObserver = new ResizeObserver(() => engine.resize())
                resizeObserver.observe(host)
                poll = window.setInterval(() => setState(engine.getState()), 250)
            }
            catch (error) {
                console.error('The orrery could not start:', error)
                if (!cancelled) setFailed(true)
            }
        }

        void start()

        return () => {
            cancelled = true
            window.clearInterval(poll)
            observer?.disconnect()
            resizeObserver?.disconnect()
            engineRef.current?.dispose()
            engineRef.current = null
        }
    }, [select, variant])

    useEffect(() => {
        if (variant === 'page') return

        engineRef.current?.setWheelZoom(engaged)

        if (!engaged) return

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setEngaged(false)
        }

        window.addEventListener('keydown', handleKey)

        return () => window.removeEventListener('keydown', handleKey)
    }, [engaged, variant])

    const focusedBody = focused ? bodyByKey(focused) : null
    const distances = focused && state ? state.distances[focused] : null
    const planets = SOLAR_BODIES.filter(body => body.kind === 'Planet')
    const dwarfs = SOLAR_BODIES.filter(body => body.kind === 'Dwarf planet')

    const togglePause = () => {
        const next = !paused

        setPaused(next)
        engineRef.current?.setPaused(next)
    }

    const changeScale = (mode: 'readable' | 'true') => {
        setScale(mode)
        engineRef.current?.setScaleMode(mode)
    }

    const toggleMissions = () => {
        const next = !missions

        setMissions(next)
        engineRef.current?.setMissions(next)

        if (next) select(null)
    }

    return (
        <section
            id={variant === 'home' ? 'solar-system' : undefined}
            aria-labelledby='orrery-title'
            className={cn('relative w-full overflow-hidden bg-black', variant === 'home' ? 'h-[100svh] min-h-[40rem]' : 'h-[100svh] min-h-[36rem]')}
        >
            <div
                ref={hostRef}
                data-cursor='drag'
                onPointerLeave={() => {
                    if (variant === 'home') setEngaged(false)
                }}
                className={cn('absolute inset-0 select-none', variant === 'page' ? 'touch-none' : 'touch-pan-y')}
            >
                <div className='media absolute inset-0'>
                    <canvas ref={canvasRef} role='img' aria-label='A 3D model of the solar system with the planets in their real current positions' className='block h-full w-full' />
                </div>

                {failed && (
                    <div className='absolute inset-0 flex items-center justify-center p-8 text-center'>
                        <p className='max-w-sm text-star-2'>
                            This browser cannot draw the 3D orrery. The planet facts are still below.
                        </p>
                    </div>
                )}

                <div className='pointer-events-none absolute inset-0'>
                    {SOLAR_BODIES.map(body => (
                        <button
                            key={body.key}
                            ref={element => {
                                if (element) labelRefs.current.set(body.key, element)
                                else labelRefs.current.delete(body.key)
                            }}
                            type='button'
                            onClick={() => select(body.key)}
                            className='pointer-events-auto absolute left-0 top-0 opacity-0 transition-opacity duration-300'
                            aria-label={`Fly to ${body.name}`}
                        >
                            <span className={cn('absolute -translate-x-1/2 -translate-y-1/2 rounded-full border', body.kind === 'Dwarf planet' ? 'size-2 border-star-3/60' : 'size-3 border-brass/70', focused === body.key && 'border-brass')} />
                            <span className={cn('label-mono absolute left-3 top-1 whitespace-nowrap text-[0.6rem]', body.kind === 'Dwarf planet' ? 'text-star-3' : 'text-star-2', focused === body.key && 'text-brass')}>
                                {body.key === 'sun' ? 'Sun' : body.name}
                            </span>
                        </button>
                    ))}
                    {MISSIONS.map(mission => (
                        <span
                            key={mission.key}
                            ref={element => {
                                if (element) labelRefs.current.set(`mission:${mission.key}`, element)
                                else labelRefs.current.delete(`mission:${mission.key}`)
                            }}
                            className='absolute left-0 top-0 opacity-0'
                        >
                            <span className='label-mono absolute left-3 top-1 whitespace-nowrap text-[0.6rem]' style={{ color: mission.colour }}>
                                {`${mission.name} · launched ${formatLaunch(mission.launch)}`}
                                {state?.missions[mission.key]?.active && (
                                    <span className='block text-star-3'>
                                        {`${state.missions[mission.key].earth.toFixed(1)} AU from Earth`}
                                    </span>
                                )}
                            </span>
                        </span>
                    ))}
                </div>
            </div>

            <div className={cn('pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/80 to-transparent pb-16', variant === 'home' ? 'pt-24' : 'pt-20')}>
                <div className='container-page flex flex-wrap items-start justify-between gap-6'>
                    <div className={cn(variant === 'page' && 'lg:pl-[19rem]')}>
                        <p className='label-mono text-brass'>
                            {variant === 'home' ? 'The Solar System' : 'Orrery'}
                        </p>
                        {variant === 'home' ? (
                            <h2 id='orrery-title' className='display-serif mt-3 max-w-xl text-4xl text-star md:text-6xl'>
                                Everything that circles our star
                            </h2>
                        ) : (
                            <h1 id='orrery-title' className='display-serif mt-3 max-w-xl text-4xl text-star md:text-6xl'>
                                The Solar System, right now
                            </h1>
                        )}
                        <p className='data-mono mt-3 text-star-2'>
                            {state ? `${simDateFormatter.format(new Date(state.simTime))} · ${simTimeFormatter.format(new Date(state.simTime))}` : 'Placing the planets'}
                        </p>
                    </div>
                    {variant === 'home' && (
                        <FadeLink href='/solar-system' className='pointer-events-auto group mt-2 inline-flex items-center gap-3 text-star'>
                            <span className='label-mono'>
                                Full orrery with moons and missions
                            </span>
                            <ArrowUpRight aria-hidden className='size-4 text-brass transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' strokeWidth={1.25} />
                        </FadeLink>
                    )}
                </div>
            </div>

            {variant === 'page' && (
                <>
                    <button
                        type='button'
                        onClick={() => setListOpen(value => !value)}
                        aria-expanded={listOpen}
                        className='label-mono absolute left-5 top-[11.5rem] z-10 inline-flex h-9 items-center rounded-full border border-hair-2 bg-black/60 px-4 text-star backdrop-blur lg:hidden'
                    >
                        {listOpen ? 'Hide the list' : 'Planets and moons'}
                    </button>
                    <nav
                        aria-label='Planets and moons'
                        className={cn(
                            'absolute bottom-28 left-0 top-[14rem] z-10 w-[18rem] overflow-y-auto border-r border-hair bg-black/70 px-5 py-4 backdrop-blur-md transition-transform duration-500 lg:top-24 lg:translate-x-0 lg:bg-black/40',
                            listOpen ? 'translate-x-0' : '-translate-x-full'
                        )}
                    >
                        <button
                            type='button'
                            onClick={() => select('sun')}
                            className={cn('block w-full py-2 text-left font-heading text-xl transition-colors', focused === 'sun' ? 'text-brass' : 'text-star hover:text-brass')}
                        >
                            The Sun
                        </button>
                        <p className='label-mono mt-4 text-star-3'>
                            Planets
                        </p>
                        <ul className='mt-1'>
                            {planets.map(body => {
                                const moons = MOONS.filter(item => item.parent === body.key)

                                return (
                                    <li key={body.key} className='border-b border-hair py-2'>
                                        <button
                                            type='button'
                                            onClick={() => select(body.key)}
                                            className={cn('w-full text-left font-heading text-xl transition-colors', focused === body.key ? 'text-brass' : 'text-star hover:text-brass')}
                                        >
                                            {body.name}
                                        </button>
                                        {moons.length > 0 && (
                                            <ul className='mt-1 flex flex-wrap gap-x-3 gap-y-1'>
                                                {moons.map(item => (
                                                    <li key={item.name}>
                                                        <button
                                                            type='button'
                                                            onClick={() => {
                                                                select(item.parent)
                                                                setMoon(item)
                                                            }}
                                                            className={cn('label-mono text-[0.6rem] transition-colors', moon?.name === item.name ? 'text-brass' : 'text-star-3 hover:text-star')}
                                                        >
                                                            {item.name}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                )
                            })}
                        </ul>
                        <p className='label-mono mt-5 text-star-3'>
                            Dwarf planets
                        </p>
                        <ul className='mt-1'>
                            {dwarfs.map(body => {
                                const moons = MOONS.filter(item => item.parent === body.key)

                                return (
                                    <li key={body.key} className='border-b border-hair py-2'>
                                        <button
                                            type='button'
                                            onClick={() => select(body.key)}
                                            className={cn('w-full text-left font-heading text-lg transition-colors', focused === body.key ? 'text-brass' : 'text-star-2 hover:text-brass')}
                                        >
                                            {body.name}
                                        </button>
                                        {moons.map(item => (
                                            <button
                                                key={item.name}
                                                type='button'
                                                onClick={() => {
                                                    select(item.parent)
                                                    setMoon(item)
                                                }}
                                                className={cn('label-mono mr-3 text-[0.6rem] transition-colors', moon?.name === item.name ? 'text-brass' : 'text-star-3 hover:text-star')}
                                            >
                                                {item.name}
                                            </button>
                                        ))}
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>
                </>
            )}

            {(focusedBody || moon) && (
                <aside
                    aria-live='polite'
                    className='absolute inset-x-3 bottom-24 z-30 max-h-[60svh] md:z-20 overflow-y-auto border border-hair bg-black/75 p-5 backdrop-blur-md md:inset-x-auto md:bottom-28 md:right-8 md:top-28 md:max-h-none md:w-[22rem] md:p-6'
                >
                    <div className='flex items-start justify-between gap-4'>
                        <div>
                            <p className='label-mono text-brass'>
                                {moon ? `Moon of ${bodyByKey(moon.parent)?.name ?? ''}` : focusedBody?.kind}
                            </p>
                            <h3 className='mt-1 font-heading text-4xl font-light text-star'>
                                {moon ? moon.name : focusedBody?.name}
                            </h3>
                        </div>
                        <button
                            type='button'
                            onClick={() => select(null)}
                            aria-label='Close and return to the whole system'
                            className='inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-hair-2 text-star-2 hover:text-star'
                        >
                            <X aria-hidden className='size-4' strokeWidth={1.25} />
                        </button>
                    </div>

                    {moon ? (
                        <>
                            <p className='mt-4 text-star-2'>
                                {moon.note}
                            </p>
                            <dl className='mt-4'>
                                <div className='flex justify-between gap-4 border-b border-hair py-2.5'>
                                    <dt className='label-mono text-star-3'>
                                        Diameter
                                    </dt>
                                    <dd className='data-mono text-star'>
                                        {moon.diameter}
                                    </dd>
                                </div>
                                <div className='flex justify-between gap-4 border-b border-hair py-2.5'>
                                    <dt className='label-mono text-star-3'>
                                        One orbit
                                    </dt>
                                    <dd className='data-mono text-star'>
                                        {moon.orbit}
                                    </dd>
                                </div>
                            </dl>
                            <button type='button' onClick={() => setMoon(null)} className='label-mono mt-5 text-star-2 hover:text-star'>
                                {`Back to ${bodyByKey(moon.parent)?.name ?? 'the planet'}`}
                            </button>
                        </>
                    ) : focusedBody && (
                        <>
                            <div className='mx-auto mt-4 w-40 md:w-44'>
                                <PlanetGlobe bodyKey={focusedBody.key} />
                            </div>
                            <p className='mt-4 text-[0.95rem] text-star-2'>
                                {focusedBody.summary}
                            </p>
                            <dl className='mt-4'>
                                {[
                                    ['Diameter', focusedBody.diameter],
                                    ['One day', focusedBody.day],
                                    ['One year', focusedBody.year],
                                    [focusedBody.key === 'sun' ? 'Family' : 'Moons', focusedBody.moons],
                                    ['Temperature', focusedBody.temperature]
                                ].map(([term, value]) => (
                                    <div key={term} className='flex justify-between gap-4 border-b border-hair py-2.5'>
                                        <dt className='label-mono shrink-0 text-star-3'>
                                            {term}
                                        </dt>
                                        <dd className='data-mono text-right text-star'>
                                            {value}
                                        </dd>
                                    </div>
                                ))}
                                {distances && focusedBody.key !== 'sun' && (
                                    <div className='flex justify-between gap-4 border-b border-hair py-2.5'>
                                        <dt className='label-mono shrink-0 text-star-3'>
                                            From the Sun
                                        </dt>
                                        <dd className='data-mono text-right text-star'>
                                            {`${formatAu(distances.sun)} AU`}
                                        </dd>
                                    </div>
                                )}
                                {distances && focusedBody.key !== 'earth' && (
                                    <div className='flex justify-between gap-4 py-2.5'>
                                        <dt className='label-mono shrink-0 text-star-3'>
                                            From Earth, live
                                        </dt>
                                        <dd className='data-mono text-right text-brass'>
                                            {`${formatAu(distances.earth)} AU`}
                                            <span className='block text-[0.7rem] text-star-3'>
                                                {`${Math.round((distances.earth * AU_KM) / 1e6).toLocaleString('en-GB')} million km · ${lightTime(distances.earth)}`}
                                            </span>
                                        </dd>
                                    </div>
                                )}
                            </dl>
                        </>
                    )}
                </aside>
            )}

            <div className='absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/90 via-black/60 to-transparent pt-10'>
                <div className='container-page flex flex-wrap items-center gap-x-6 gap-y-3 pb-5'>
                    <div className='flex items-center gap-2'>
                        <button
                            type='button'
                            onClick={togglePause}
                            aria-label={paused ? 'Start time' : 'Stop time'}
                            className='inline-flex size-10 items-center justify-center rounded-full border border-hair-2 text-star transition-colors hover:border-brass'
                        >
                            {paused ? <Play aria-hidden className='size-4' strokeWidth={1.25} /> : <Pause aria-hidden className='size-4' strokeWidth={1.25} />}
                        </button>
                        <button
                            type='button'
                            onClick={() => engineRef.current?.resetToNow()}
                            className='label-mono inline-flex h-10 items-center gap-2 rounded-full border border-hair-2 px-4 text-star-2 transition-colors hover:border-brass hover:text-star'
                        >
                            <RotateCcw aria-hidden className='size-3.5' strokeWidth={1.25} />
                            Today
                        </button>
                    </div>

                    <div className='min-w-[13rem] flex-1 sm:max-w-sm'>
                        <label htmlFor={`orrery-speed-${variant}`} className='label-mono flex justify-between text-star-3'>
                            <span>
                                Speed
                            </span>
                            <span className='text-star-2'>
                                {describeRate(rateFor(speed))}
                            </span>
                        </label>
                        <input
                            id={`orrery-speed-${variant}`}
                            type='range'
                            className='instrument-range'
                            min={0}
                            max={100}
                            step={1}
                            value={speed}
                            onChange={event => {
                                const value = Number(event.target.value)

                                setSpeed(value)
                                engineRef.current?.setSpeed(rateFor(value))
                            }}
                            aria-valuetext={describeRate(rateFor(speed))}
                        />
                    </div>

                    <div role='radiogroup' aria-label='Scale' className='flex items-center rounded-full border border-hair-2 p-1'>
                        {(['readable', 'true'] as const).map(mode => (
                            <button
                                key={mode}
                                type='button'
                                role='radio'
                                aria-checked={scale === mode}
                                onClick={() => changeScale(mode)}
                                className={cn('label-mono h-8 rounded-full px-4 transition-colors duration-500', scale === mode ? 'bg-brass text-ink' : 'text-star-2 hover:text-star')}
                            >
                                {mode === 'readable' ? 'Readable scale' : 'True scale'}
                            </button>
                        ))}
                    </div>

                    {variant === 'page' && (
                        <button
                            type='button'
                            aria-pressed={missions}
                            onClick={toggleMissions}
                            className={cn('label-mono inline-flex h-10 items-center rounded-full border px-4 transition-colors duration-500', missions ? 'border-brass bg-brass/15 text-star' : 'border-hair-2 text-star-2 hover:text-star')}
                        >
                            {missions ? 'Missions on' : 'Mission paths'}
                        </button>
                    )}

                    <div className='ml-auto flex items-center gap-2'>
                        <p className='label-mono mr-2 hidden text-star-3 xl:block'>
                            {variant === 'home' && !engaged ? 'Drag to turn · click a planet · click inside to zoom' : 'Drag to turn · scroll or pinch to zoom'}
                        </p>
                        <button
                            type='button'
                            onClick={() => engineRef.current?.zoom(0.7)}
                            aria-label='Zoom in'
                            className='inline-flex size-10 items-center justify-center rounded-full border border-hair-2 text-star transition-colors hover:border-brass'
                        >
                            <Plus aria-hidden className='size-4' strokeWidth={1.25} />
                        </button>
                        <button
                            type='button'
                            onClick={() => engineRef.current?.zoom(1.4)}
                            aria-label='Zoom out'
                            className='inline-flex size-10 items-center justify-center rounded-full border border-hair-2 text-star transition-colors hover:border-brass'
                        >
                            <Minus aria-hidden className='size-4' strokeWidth={1.25} />
                        </button>
                    </div>
                </div>

                {scale === 'true' && (
                    <p className='container-page -mt-2 pb-4 text-sm text-star-3'>
                        At true scale the planets are far too small to see: the labels mark where they are. Click one to fly in to its real size.
                    </p>
                )}
            </div>
        </section>
    )
}
